import { useState, useEffect } from "react";
import { Shield, ShieldCheck, ShieldAlert, AlertTriangle, Car, DollarSign, ChevronDown, ChevronUp, MapPin } from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { useWidgetMode } from "@/lib/widgetMode";

const StatusBadge = ({ status, label, count }) => {
  const isClear = status === "clear";
  return (
    <div className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl text-sm ${
      isClear ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"
    }`}>
      {isClear ? (
        <ShieldCheck className="w-4 h-4 flex-shrink-0" />
      ) : (
        <ShieldAlert className="w-4 h-4 flex-shrink-0" />
      )}
      <span className="font-medium">{label}</span>
      <span className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
        isClear ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
      }`}>
        {isClear ? "Clear" : `${count} Record${count > 1 ? "s" : ""}`}
      </span>
    </div>
  );
};

const SaleRecord = ({ record }) => (
  <div className="flex items-start justify-between py-3 border-b border-slate-100 last:border-0">
    <div>
      <p className="text-sm font-medium text-slate-900">{record.seller_name || "Private Seller"}</p>
      <div className="flex items-center gap-1.5 text-xs text-slate-500 mt-0.5">
        {record.seller_city && (
          <>
            <MapPin className="w-3 h-3" />
            <span>{record.seller_city}, {record.seller_state}</span>
          </>
        )}
        {record.seller_type && <span className="ml-1">({record.seller_type})</span>}
      </div>
    </div>
    <div className="text-right">
      {record.listing_price && (
        <p className="text-sm font-semibold text-slate-900">{record.listing_price}</p>
      )}
      <p className="text-xs text-slate-500">{record.date}</p>
    </div>
  </div>
);

const VehicleHistoryTile = ({ vin }) => {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { isCompact: compact } = useWidgetMode();

  useEffect(() => {
    if (!vin) return;
    let cancelled = false;
    const fetchHistory = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/${vin}/history`);
        if (!cancelled) setHistory(res.data);
      } catch {
        if (!cancelled) setHistory(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchHistory();
    return () => { cancelled = true; };
  }, [vin]);

  if (loading) {
    return (
      <div className="surface-card p-6" data-testid="vehicle-history-tile-loading">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl skeleton" />
          <div className="flex-1">
            <div className="h-4 w-40 skeleton rounded mb-2" />
            <div className="h-3 w-56 skeleton rounded" />
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-10 skeleton rounded-xl" />
          <div className="h-10 skeleton rounded-xl" />
          <div className="h-10 skeleton rounded-xl" />
          <div className="h-10 skeleton rounded-xl" />
        </div>
      </div>
    );
  }

  if (!history) return null;

  const allClear = history.theft?.status === "clear" &&
                   history.salvage?.status === "clear" &&
                   history.accident?.status === "clear";

  const isMock = history.source === "mock";
  const salesRecords = history.sale?.records || [];
  const px = compact ? "px-4" : "px-6";

  return (
    <div className="surface-card overflow-hidden" data-testid="vehicle-history-tile">
      {/* Header */}
      <div className={`${px} ${compact ? "pt-4 pb-3" : "pt-5 pb-4"}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: allClear ? '#f0fdf4' : '#fffbeb' }}>
            <Shield className={`w-5 h-5 ${allClear ? "text-emerald-600" : "text-amber-600"}`} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-slate-900">Vehicle History Report</h3>
              {isMock && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 font-medium">Preview</span>
              )}
            </div>
            <p className="text-sm text-slate-500 mt-0.5">
              {allClear ? "No issues found — this vehicle has a clean history" : "Review flagged records below"}
            </p>
          </div>
        </div>
      </div>

      {/* Status Grid — a fixed 2-up grid doesn't leave enough room per badge
          in a narrow compact embed (label + count badge get clipped), so
          stack to a single column there instead. */}
      <div className={`${px} pb-4 grid ${compact ? "grid-cols-1" : "grid-cols-2"} gap-2`}>
        <StatusBadge status={history.theft?.status} label="Theft" count={history.theft?.count} />
        <StatusBadge status={history.salvage?.status} label="Salvage" count={history.salvage?.count} />
        <StatusBadge status={history.accident?.status} label="Accident" count={history.accident?.count} />
        <StatusBadge
          status={history.sale?.count > 0 ? "records_found" : "clear"}
          label="Sales"
          count={history.sale?.count}
        />
      </div>

      {/* Expandable Sales History */}
      {salesRecords.length > 0 && (
        <div className="border-t border-slate-100">
          <button
            onClick={() => setExpanded(!expanded)}
            className={`w-full flex items-center justify-between ${px} py-3 text-sm text-slate-600 hover:bg-slate-50 transition-colors`}
            data-testid="expand-sales-history"
          >
            <span className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-slate-400" />
              <span className="font-medium">{salesRecords.length} Previous Sale{salesRecords.length > 1 ? "s" : ""} on Record</span>
            </span>
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {expanded && (
            <div className={`${px} pb-4`}>
              {salesRecords.map((record) => (
                <SaleRecord key={`${record.date}-${record.seller_name}`} record={record} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Source Footer */}
      <div className={`${px} py-3 bg-slate-50 border-t border-slate-100`}>
        <p className="text-xs text-slate-400 text-center">
          {isMock
            ? "Sample data — live history powered by VinAudit when connected"
            : "Powered by VinAudit Vehicle History"}
        </p>
      </div>
    </div>
  );
};

export default VehicleHistoryTile;
